// PurchasePlanHistoryView.js — 발주 계획 앱(project-procurement-plan)의 구매계획 이력을 전체화면으로 표시
// 발주 앱은 자기 프로젝트의 'purchasePlans' 컬렉션에 이력을 저장한다.
// 재고 앱에서 그 프로젝트를 읽기 전용으로 초기화해 이력을 가져온다 (procurementPlans.js 공용 모듈 사용).
// 2026-07 발주 앱 "저장된 구매계획" 화면 개편(발행일/결제조건/월별 결제 예상 추가)에 맞춰 재연결.
// 재고 앱은 읽기 전용 뷰이므로 발주 앱 쪽 쓰기 액션(완료체크·삭제·클라우드 업로드 등)과 하단 입력 화면 이동 버튼은 포함하지 않고,
// 발주 앱 화면 상단 요약 카드 · 저장된 구매계획 목록 · 월별 결제 예상까지만 동일하게 보여준다.
// 목록의 각 발주 행은 클릭하면 품목 상세(상품코드/상품명/발주수량/발주후재고/단가/금액)를 펼쳐 볼 수 있으나,
// 어디까지나 조회(모니터링)용이며 값을 바꾸는 입력 요소는 두지 않는다.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { History, X, RefreshCw, FileSpreadsheet, ChevronDown, ChevronRight } from "lucide-react";
import XLSXStyle from "xlsx-js-style";
import { fetchProcurementPlans, isUSD, summarizePlans, monthlyPaymentForecast, isSettledPast, currentMonth } from "./procurementPlans";

const won = (n) => (n == null ? "—" : "₩" + Math.round(Number(n)).toLocaleString("ko-KR"));
const nf = (n) => (Number(n) || 0).toLocaleString("ko-KR");
const money = (n, cur) =>
  n == null ? "—" : (isUSD(cur) ? "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 }) : won(n));

export default function PurchasePlanHistoryView({ onClose }) {
  const [plans, setPlans] = useState(null); // null = 로딩중
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({}); // 펼친 행(모니터링용 상세 — 조회 전용, 수정 불가)
  const [showSettled, setShowSettled] = useState(false); // 결제 끝난 과거 완료 건까지 볼지

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchProcurementPlans();
      setPlans(list);
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
      setPlans((prev) => prev || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 결제가 끝난 과거 건(완료 + 마지막 결제월이 지난 달 이전)은 기본으로 감춘다.
  // 데이터를 지우는 게 아니라 화면에서만 접는 것이라, 필요하면 아래 토글로 다시 펼쳐 볼 수 있다.
  const nowMonth = useMemo(() => currentMonth(), []);
  const visiblePlans = useMemo(
    () => (plans || []).filter((p) => showSettled || !isSettledPast(p, nowMonth)),
    [plans, showSettled, nowMonth]
  );
  const hiddenCount = (plans || []).length - visiblePlans.length;

  // 요약 카드는 목록과 같은 기준(보이는 건)으로 집계해야 숫자와 표가 어긋나지 않는다.
  const summary = useMemo(() => summarizePlans(visiblePlans), [visiblePlans]);
  // 월별 결제 예상은 원래도 완료 건을 빼고 계산하므로 전체 목록 기준을 그대로 쓴다.
  const forecast = useMemo(() => monthlyPaymentForecast(plans), [plans]);

  const downloadForecastExcel = () => {
    const rows = forecast.months.map((m) => ({
      "결제월": m.month,
      "KRW": m.krw ? Math.round(m.krw) : "",
      "USD": m.usd ? Number(m.usd.toFixed(2)) : "",
      "원화 환산 합계": Math.round(m.conv),
    }));
    rows.push({
      "결제월": "합계",
      "KRW": Math.round(forecast.total.krw),
      "USD": forecast.total.usd ? Number(forecast.total.usd.toFixed(2)) : "",
      "원화 환산 합계": Math.round(forecast.total.conv),
    });
    const wb = XLSXStyle.utils.book_new();
    const ws = XLSXStyle.utils.json_to_sheet(rows, { header: ["결제월", "KRW", "USD", "원화 환산 합계"] });
    XLSXStyle.utils.book_append_sheet(wb, ws, "월별결제예상");
    XLSXStyle.writeFile(wb, `월별결제예상_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const SummaryCard = ({ label, value, sub }) => (
    <div className="rounded-xl bg-slate-800 text-white p-4 shadow-sm">
      <div className="text-xs text-slate-400 mb-1.5">{label}</div>
      <div className="text-xl font-bold tabular-nums leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
      <div className="max-w-[1440px] mx-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-teal-600" />
            <h2 className="text-xl font-bold text-slate-800">구매계획 이력</h2>
            <span className="text-xs text-slate-400 ml-1">발주 계획 앱과 동일한 원본 데이터를 읽기 전용으로 표시합니다</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50"
            >
              <RefreshCw className={"h-4 w-4" + (loading ? " animate-spin" : "")} />새로고침
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
            >
              <X className="h-4 w-4" />닫기
            </button>
          </div>
        </div>

        {err && <p className="text-red-600 text-sm mb-3">불러오기 오류: {err}</p>}
        {plans === null && <p className="text-slate-500 text-sm py-10 text-center">불러오는 중…</p>}

        {plans && plans.length === 0 && !err && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500">
            아직 저장된 구매계획 이력이 없습니다.
          </div>
        )}

        {plans && plans.length > 0 && (
          <>
            {/* 상단 요약 — 발주 계획 앱의 저장된 구매계획 화면과 동일한 6개 카드 */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
              <SummaryCard label="저장된 발주" value={`${nf(summary.count)}건`} />
              <SummaryCard label="총 발주 수량" value={nf(summary.totQty)} />
              <SummaryCard label="원화(₩) 발주 합계" value={won(summary.krwAmt)} />
              <SummaryCard label="외화($) 발주 합계" value={"$" + summary.usdAmt.toLocaleString("en-US", { maximumFractionDigits: 2 })} sub={`${nf(summary.usdCount)}건`} />
              <SummaryCard label="외화 원화환산" value={won(summary.usdKrwConv)} />
              <SummaryCard label="총합계 (원화 기준)" value={won(summary.grand)} />
            </div>

            {/* 정산 완료된 과거 건 안내 + 다시 보기 토글 */}
            <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
              <span className="text-slate-500">
                {showSettled
                  ? `전체 ${nf((plans || []).length)}건 표시 중 (결제 끝난 과거 완료 건 포함)`
                  : `${nf(visiblePlans.length)}건 표시 중`}
              </span>
              {(hiddenCount > 0 || showSettled) && (
                <button
                  onClick={() => setShowSettled((v) => !v)}
                  className="font-medium text-slate-500 hover:text-slate-700 underline"
                >
                  {showSettled ? "결제 끝난 과거 건 숨기기" : `결제 끝난 과거 건 ${nf(hiddenCount)}건 보기`}
                </button>
              )}
              <span className="text-slate-400">· 완료 처리됐고 마지막 결제월이 {nowMonth} 이전인 건은 기본으로 숨깁니다</span>
            </div>

            {/* 저장된 구매계획 목록 (읽기 전용 — 완료체크/구매계획/보고서/삭제 등 발주 앱 쓰기 액션은 제외) */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600">
                    <th className="w-8 px-2 py-2"></th>
                    <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">확정일시</th>
                    <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">납기일</th>
                    <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">입항 예정일</th>
                    <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">거래처</th>
                    <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">상태</th>
                    <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">통화</th>
                    <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">총 수량</th>
                    <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">총 금액</th>
                    <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">품목수</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePlans.map((p) => {
                    const rows = Array.isArray(p.rows) ? p.rows : [];
                    const isOpen = !!open[p._id];
                    return (
                      <React.Fragment key={p._id}>
                        <tr
                          className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => setOpen((o) => ({ ...o, [p._id]: !o[p._id] }))}
                        >
                          <td className="px-2 py-2 text-slate-400">
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{p.confirmedAt || "—"}</td>
                          {/* 재고관리·발주 산출의 입고 예정 배치는 이 납기일(date)을 기준으로 한다(2026-09-08, 발주 앱의 '납기' 필드).
                              date가 비어 있는 발주만 입항 예정일(etaDate)로 대체된다 — 그 경우 회색으로 표시. */}
                          <td className="px-3 py-2 whitespace-nowrap font-medium"
                              title={p.date?"납기일 — 재고·발주 입고 계산에 사용":"납기일 미입력 — 입항 예정일로 대체해 계산"}>
                            {p.date||<span className="text-slate-400">{p.etaDate||"—"}</span>}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-500" title="입항 예정일 — 참고용">{p.etaDate || "—"}</td>
                          <td className="px-3 py-2">{p.vendor || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={"inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold "+(p.done?"bg-slate-100 text-slate-500":"bg-teal-100 text-teal-700")}>
                              {p.done?"완료":"진행중"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{p.cur || "—"}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{nf(p.totQty)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{money(p.totAmt, p.cur)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{rows.length}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50/60">
                            <td></td>
                            <td colSpan={9} className="px-3 pb-3 pt-1">
                              {/* 조회 전용 상세 — 입력/수정 요소 없음, 클릭해도 값이 바뀌지 않는 모니터링 표 */}
                              <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-slate-100 text-slate-500">
                                      <th className="text-left font-semibold px-2 py-1.5">상품코드</th>
                                      <th className="text-center font-semibold px-2 py-1.5">상품명</th>
                                      <th className="text-right font-semibold px-2 py-1.5">발주수량</th>
                                      <th className="text-right font-semibold px-2 py-1.5">발주후재고</th>
                                      <th className="text-right font-semibold px-2 py-1.5">단가</th>
                                      <th className="text-right font-semibold px-2 py-1.5">금액</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((r, i) => (
                                      <tr key={i} className="border-t border-slate-100">
                                        <td className="px-2 py-1.5 whitespace-nowrap">{r.code ?? "—"}</td>
                                        <td className="px-2 py-1.5">{r.name ?? "—"}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">{nf(r.qty)}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">{nf(r.after)}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">{money(r.price, p.cur)}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">{money(r.amount, p.cur)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {visiblePlans.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-400">
                        표시할 발주가 없습니다 — 저장된 {nf((plans || []).length)}건이 모두 결제까지 끝난 과거 건입니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* 월별 결제 예상 — 발주 계획 앱 신규 화면과 동일 (payTerm/발행일/선적일 기준 산출, 완료 제외)
                결제는 선적일(date) 기산이 맞으므로 입항 예정일(etaDate)을 쓰지 않는다 — 재고 쪽 기준과 다름에 주의. */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 pt-3 pb-2 flex-wrap gap-y-1">
                <h3 className="text-sm font-bold text-slate-700">월별 결제 예상</h3>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-slate-400">선지급=발행일, 선지급후=납기일 기준 · 완료 제외</span>
                  <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
                  >
                    <RefreshCw className={"h-3.5 w-3.5" + (loading ? " animate-spin" : "")} />새로고침
                  </button>
                  <button
                    onClick={downloadForecastExcel}
                    className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5" />엑셀 내려받기
                  </button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600">
                    <th className="text-left font-semibold px-4 py-2 whitespace-nowrap">결제월</th>
                    <th className="text-right font-semibold px-4 py-2 whitespace-nowrap">KRW</th>
                    <th className="text-right font-semibold px-4 py-2 whitespace-nowrap">USD</th>
                    <th className="text-right font-semibold px-4 py-2 whitespace-nowrap">원화 환산 합계</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.months.map((m) => (
                    <tr key={m.month} className="border-t border-slate-100">
                      <td className="px-4 py-2 whitespace-nowrap">{m.month}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{m.krw ? nf(m.krw) : "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{m.usd ? m.usd.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{won(m.conv)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                    <td className="px-4 py-2">합계</td>
                    <td className="px-4 py-2 text-right tabular-nums">{nf(forecast.total.krw)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{forecast.total.usd.toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{won(forecast.total.conv)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
